/**
 * colleges.js — 수원대학교 단과대학 / 학부 / 전공 메타데이터
 *
 * 이 파일은 학과 계층 구조(단과대 → 학부 → 전공)와
 * UI 표시용 부가 정보(아이콘, 상태)를 관리한다.
 *
 * hasData: 이수요건 데이터가 실제 입력된 학과면 true
 */

export const COLLEGES = [
  // ── 경영공학대학 ────────────────────────────────────────────────────────────
  // PDF(2026_credits.pdf) 공식 명칭: 경영공학대학
  {
    collegeId:      'business_engineering',
    collegeName:    '경영공학대학',
    pdfCollegeName: '경영공학대학',
    departments: [
      {
        departmentId:   'business',
        departmentName: '경영공학학부',
        pdfDeptName:    '경영',
        icon:           '📊',
        hasData:        true,
        graduationRuleId: 'business_admin',
        majors: [
          { majorId: 'business_admin', majorName: '경영학전공' },
          { majorId: 'global_biz',     majorName: '글로벌비즈니스전공' },
          { majorId: 'accounting',     majorName: '회계전공' },
        ],
      },
    ],
  },

  // ── 지능형SW대학 ─────────────────────────────────────────────────────────────
  // PDF(2026_credits.pdf) 공식 명칭: 지능형SW대학 (기존 코드: 'IT대학')
  {
    collegeId:      'it',
    collegeName:    '지능형SW대학',
    pdfCollegeName: '지능형SW대학',
    departments: [
      {
        departmentId:     'cs',
        departmentName:   '컴퓨터공학부',
        pdfDeptName:      '컴퓨터공학',
        icon:             '💻',
        hasData:          true,
        graduationRuleId: 'cs',
        majors: [
          { majorId: 'cs_main', majorName: '컴퓨터공학전공' },
        ],
      },
      {
        departmentId:     'ai_sw',
        departmentName:   'AI·소프트웨어학부',
        pdfDeptName:      'AI소프트웨어',
        icon:             '🤖',
        hasData:          true,
        graduationRuleId: 'ai_sw',
        majors: [
          { majorId: 'ai_sw_main', majorName: 'AI소프트웨어전공' },
          { majorId: 'ai_bigdata', majorName: 'AI빅데이터전공' },
        ],
      },
    ],
  },

  // ── 혁신공과대학 ─────────────────────────────────────────────────────────────
  // PDF(2026_credits.pdf) 공식 명칭: 혁신공과대학 (기존 코드: '공과대학')
  {
    collegeId:      'engineering',
    collegeName:    '혁신공과대학',
    pdfCollegeName: '혁신공과대학',
    departments: [
      {
        departmentId:     'elec',
        departmentName:   '전자공학부',
        pdfDeptName:      '전자공학',
        icon:             '⚡',
        hasData:          true,
        graduationRuleId: 'electronic_eng',
        majors: [
          { majorId: 'elec_main', majorName: '전자공학전공' },
          { majorId: 'elec_ee',   majorName: '전기공학전공' },
        ],
      },
      {
        departmentId:     'mech',
        departmentName:   '기계공학부',
        pdfDeptName:      '기계공학부',
        icon:             '⚙️',
        hasData:          true,
        graduationRuleId: 'mechanical_eng',
        majors: [
          { majorId: 'mech_main', majorName: '기계공학전공' },
        ],
      },
      {
        departmentId:     'chem',
        departmentName:   '화학공학과',
        pdfDeptName:      '화학공학',
        icon:             '🧪',
        hasData:          true,
        graduationRuleId: 'chemical_eng',
        majors: [
          { majorId: 'chem_main',     majorName: '화학공학전공' },
          { majorId: 'new_materials', majorName: '신소재공학전공' },
        ],
      },
      {
        departmentId:     'civil',
        departmentName:   '토목환경공학부',
        pdfDeptName:      '건설환경에너지공학부',
        icon:             '🏗️',
        hasData:          false,
        graduationRuleId: 'civil_env_eng',
        majors: [
          { majorId: 'civil_ce',  majorName: '건설환경공학전공' },
          { majorId: 'civil_env', majorName: '환경에너지공학전공' },
        ],
      },
    ],
  },

  // ── 디자인앤아트대학 ─────────────────────────────────────────────────────────
  // PDF(2026_credits.pdf) 공식 명칭: 디자인앤아트대학 (기존 코드: '예술대학')
  {
    collegeId:      'arts',
    collegeName:    '디자인앤아트대학',
    pdfCollegeName: '디자인앤아트대학',
    departments: [
      {
        departmentId:     'design',
        departmentName:   '디자인학부',
        pdfDeptName:      '디자인학부',
        icon:             '🎨',
        hasData:          true,
        graduationRuleId: 'design',
        majors: [
          { majorId: 'comm_design',    majorName: '커뮤니케이션디자인전공' },
          { majorId: 'fashion_design', majorName: '패션디자인전공' },
          { majorId: 'craft_design',   majorName: '공예디자인전공' },
        ],
      },
    ],
  },
]

/**
 * departmentName → { collegeId, collegeName, departmentId, icon, hasData, majors }
 * UI 선택값(departmentName)으로 단과대·학부 정보를 빠르게 조회할 수 있는 플랫 맵
 */
export const DEPT_META_MAP = Object.fromEntries(
  COLLEGES.flatMap(col =>
    col.departments.map(dept => [
      dept.departmentName,
      {
        collegeId:      col.collegeId,
        collegeName:    col.collegeName,
        departmentId:   dept.departmentId,
        departmentName: dept.departmentName,
        icon:           dept.icon,
        hasData:        dept.hasData,
        majors:         dept.majors,
      },
    ])
  )
)
